<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Chip, ListItem, Stack, Text } from '@Shelly/shelly-design-system';
	import { devices, devices_settings, devices_status } from 'src/stores';
	import { isThreeEM } from 'src/helpers/interface_helpers';
	import { getConfiguredEm3ChannelName } from 'src/helpers/em3_channel_names';
	import {
		buildPhaseHealth,
		type PhaseHealthSummary,
	} from 'src/components/EnergyMonitoring/helpers/energy_phase_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { DEMO_PHASE_HEALTH } from 'src/components/EnergyMonitoring/helpers/energy_demo_providers';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmWidgetState from '../EmWidgetState.svelte';
	import { ENERGY_ADD_DEVICE_HREF } from '../helpers/energy_widget_state_helpers';

	export let staticPreview = false;
	let summary: PhaseHealthSummary = DEMO_PHASE_HEALTH;

	$: useDemoData = staticPreview || $energyMonitoringDataMode === 'demo';

	$: em3Devices = Object.values($devices).filter(
		(d) => d && isThreeEM(d.type ?? '') && d.mode !== 'monophase',
	);
	$: hasEm3 = useDemoData || em3Devices.length > 0;
	$: {
		$devices_settings;
		summary = useDemoData
			? DEMO_PHASE_HEALTH
			: buildPhaseHealth(em3Devices, $devices_status, getConfiguredEm3ChannelName);
	}
	$: phases = summary.phases;
	$: hasReadings = phases.some((p) => p.power > 0);

	const phaseProgressColor = (tone: 'primary' | 'success' | 'warning') => {
		if (tone === 'warning') return 'warning';
		if (tone === 'success') return 'success';
		return 'heating';
	};
</script>

<EmWidgetShell
	widgetClass="energy-widget-phase"
	title={$format('energy_monitoring_phase_health_title', { default: 'Phase health' })}
	infoText={$format('energy_monitoring_phase_info', {
		default:
			'Live load balance across phases A, B, and C. Bars show each phase relative to the busiest phase; imbalance above 15% is flagged.',
	})}
>
	<svelte:fragment slot="actions">
		{#if summary.sourceDeviceName}
			<Text styleType="miniature" class="text-color-muted">{summary.sourceDeviceName}</Text>
		{:else}
			<Text styleType="miniature" class="text-color-muted">
				{$format('energy_monitoring_phase_subtitle', { default: '3EM' })}
			</Text>
		{/if}
	</svelte:fragment>

	{#if hasEm3 && phases.length && hasReadings}
		<div class="em-phase-body">
			<div class="em-phase-summary">
				<div class="em-phase-total">
					<Text styleType="miniature" class="text-color-muted">
						{$format('energy_monitoring_phase_total', { default: 'Total load' })}
					</Text>
					<Text styleType="h2" class="text-600">{summary.totalLabel}</Text>
				</div>
				<Chip
					variant={summary.isBalanced ? 'success' : 'warning'}
					size="m"
					aria-label={summary.isBalanced
						? $format('camera_nv_balanced', { default: 'Balanced' })
						: $format('energy_monitoring_phase_imbalanced', {
								default: 'Imbalanced {percent}%',
								values: { percent: summary.imbalancePercent },
							})}
				>
					{summary.isBalanced
						? $format('camera_nv_balanced', { default: 'Balanced' })
						: $format('energy_monitoring_phase_imbalanced', {
								default: 'Imbalanced {percent}%',
								values: { percent: summary.imbalancePercent },
							})}
				</Chip>
			</div>

			<Stack gap="xs">
				{#each phases as phase (phase.phase)}
					<ListItem
						viewType="bar"
						title={phase.displayName ||
							$format('energy_monitoring_phase_label', {
								default: 'Phase {phase}',
								values: { phase: phase.phase },
							})}
						progress={{
							percent: phase.percent,
							title: phase.label,
							color: phaseProgressColor(phase.tone),
							showPercentBar: true,
						}}
						a11yProps={{ progressbarAriaLabel: phase.label }}
					/>
				{/each}
			</Stack>
		</div>
	{:else if hasEm3}
		<EmWidgetState
			message={$format('energy_monitoring_phase_waiting', {
				default: 'No phase activity to display.',
			})}
			minHeight="160px"
		/>
	{:else}
		<EmWidgetState
			kind="setup"
			message={$format('energy_monitoring_state_phase_setup', {
				default: 'Add a 3-phase power meter to see phase health.',
			})}
			minHeight="160px"
			actionHref={ENERGY_ADD_DEVICE_HREF}
			actionLabel={$format('button_add_device', { default: 'Add device' })}
		/>
	{/if}
</EmWidgetShell>

<style>
	.em-phase-body {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
	}

	.em-phase-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-s);
	}

	.em-phase-total {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}
</style>
